import { zodResolver } from "@hookform/resolvers/zod";
import { Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "sonner";

import FormDialog from "@/components/reusables/FormDialog";
import { FormSelectSingle } from "@/components/reusables/custom-form-fields/FormSelect";
import FormWrapper from "@/components/reusables/custom-form-fields/FormWrapper";
import InputField from "@/components/reusables/custom-form-fields/InputField";
import ButtonWithIcon from "@/components/ui/ButtonWithIcon";
import { Button } from "@/components/ui/button";
import { Form, FormField } from "@/components/ui/form";
import { Button_Names } from "@/constants/global-constants";
import {
  SpecialRateFor,
  SpecialRateLocalKeys,
  SpecialRateToastMessages,
} from "@/constants/special-rate.constants";
import {
  useGetCustomerSpecialRateQuery,
  useSpecialRateMutation,
} from "@/redux/api/clientQuery";
import { useGetItemsQuery } from "@/redux/api/itemQuery";
import {
  useGetProjectSpecialRateQuery,
  useSpecialRateForProjectMutation,
} from "@/redux/api/projectsQuery";
import {
  SpecialRate,
  emptySpecialRate,
  specialRateSchema,
} from "@/schemas/special-rate.schema";
import { validateResponse } from "@/utils/helper-functions/form.helper";

interface SpecialRateModalProps {
  isOpen: boolean;
  toggleModal: () => void;
  forType?: SpecialRateFor;
}

const SpecialRateModal = ({
  isOpen,
  toggleModal,
  forType,
}: SpecialRateModalProps): JSX.Element => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const { data: items, isLoading } = useGetItemsQuery({});
  //This is the id of the customer or project
  const id = localStorage.getItem(
    forType === SpecialRateFor.PROJECT
      ? SpecialRateLocalKeys.PROJECT
      : SpecialRateLocalKeys.CUSTOMER,
  ) as string;
  const {
    data: customerSpecialRate,
    isLoading: isCustomerSpecialRateFetching,
    refetch,
  } = useGetCustomerSpecialRateQuery(
    {
      id,
    },
    {
      skip: forType !== SpecialRateFor.CUSTOMER || !id,
    },
  );
  const { data: projectSpecialRate, isLoading: isProjectSpecialRateFetching } =
    useGetProjectSpecialRateQuery(
      {
        id,
      },
      {
        skip: forType !== SpecialRateFor.PROJECT || !id,
      },
    );
  const [mutateCustomerSpecialRate, { isLoading: isUpdatingCustomerRate }] =
    useSpecialRateMutation();
  const [mutateSpecialRateForProject, { isLoading: isUpdatingProjectRate }] =
    useSpecialRateForProjectMutation();

  const formattedDefaultValues = useMemo(() => {
    const specialRateData =
      forType === SpecialRateFor.CUSTOMER
        ? customerSpecialRate
        : projectSpecialRate;

    return (
      specialRateData?.map((rate) => ({
        itemId: rate?.itemId ?? "",
        itemRateId: rate?.id ?? "",
        specialRate: rate?.projectPrice ?? rate?.customerPrice ?? 0,
        defaultRate: rate?.item?.defaultPrice,
        currentRate: rate?.item?.currentPrice,
      })) ?? []
    );
  }, [customerSpecialRate, projectSpecialRate, forType]);

  const isForUpdate = formattedDefaultValues.length > 0;

  const form = useForm<SpecialRate>({
    resolver: zodResolver(specialRateSchema),
    mode: "onChange",
    values: {
      rates:
        formattedDefaultValues.length > 0
          ? formattedDefaultValues
          : emptySpecialRate,
    },
    defaultValues: {
      rates: emptySpecialRate,
    },
  });

  // const emptyField = form.getValues("deleteRateIds");
  const emptyField =
    form.watch("rates").length === 0 &&
    typeof form.getValues("deleteRateIds") === "undefined";

  const { fields, remove, append } = useFieldArray({
    name: "rates",
    control: form.control,
  });

  const handelClose = (): void => {
    form.reset();
    toggleModal();
    localStorage.removeItem(
      forType === SpecialRateFor.PROJECT
        ? SpecialRateLocalKeys.PROJECT
        : SpecialRateLocalKeys.CUSTOMER,
    );
  };

  const handleRemove = (id: string, index: number): void => {
    if (id) {
      const existingDeletedRateIds = form.getValues("deleteRateIds") || [];
      form.setValue("deleteRateIds", [...existingDeletedRateIds, id]);
    }
    // Remove the corresponding item from selected items
    setSelectedItems((prev) => prev.filter((_, i) => i !== index));
    remove(index);
  };

  // Update selected items when an item is selected or deselected
  const handleSelectChange = (value: string, index: number): void => {
    const prevSelectedItems = [...selectedItems];
    // Remove previously selected item for this row, if any
    const previousValue = form.getValues(`rates.${index}.itemId`);
    if (previousValue) {
      const previousIndex = prevSelectedItems.indexOf(previousValue);
      if (previousIndex !== -1) {
        prevSelectedItems.splice(previousIndex, 1);
      }
    }
    // Add the new value to the selected items
    if (value) {
      prevSelectedItems[index] = value;
    }
    setSelectedItems(prevSelectedItems);

    const selectedItem = items?.data?.find((item) => item.id === value);
    if (selectedItem) {
      form.setValue(`rates.${index}.itemId`, selectedItem.id, {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.setValue(`rates.${index}.defaultRate`, selectedItem.defaultPrice, {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.setValue(`rates.${index}.currentRate`, selectedItem.currentPrice, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const actionBtnName = isForUpdate
    ? Button_Names.Save_Changes
    : "Save Special Rates";
  const resetBtnName = isForUpdate ? Button_Names.Discard_Change : "Reset";

  const onSubmit = async (values: SpecialRate): Promise<void> => {
    const cleanedArray = values?.rates?.map(({ itemRateId, ...rest }) =>
      itemRateId === "" ? rest : { itemRateId, ...rest },
    );
    const mutation =
      forType === SpecialRateFor.CUSTOMER
        ? mutateCustomerSpecialRate
        : mutateSpecialRateForProject;
    const response = await mutation({
      id,
      data: {
        ...values,
        rates: cleanedArray,
      },
    });
    validateResponse({
      response,
      onSuccess: async () => {
        toast.success(
          isForUpdate
            ? SpecialRateToastMessages?.UPDATE
            : SpecialRateToastMessages?.CREATE,
        );
        if (forType === SpecialRateFor.CUSTOMER) {
          await refetch();
        }
        handelClose();
      },
    });
  };

  useEffect(() => {
    if (formattedDefaultValues.length > 0) {
      form.reset({ rates: formattedDefaultValues });
      setSelectedItems(
        formattedDefaultValues.map((rate) => rate.itemId as string),
      );
    }
  }, [formattedDefaultValues, form]);

  return (
    <Form {...form}>
      <FormDialog
        isOpen={isOpen}
        toggleModal={handelClose}
        title='Special Rate'
        isLoading={
          isLoading ||
          isCustomerSpecialRateFetching ||
          isProjectSpecialRateFetching
        }
        isFormDirty={Object.keys(form.formState.dirtyFields).length > 0}
        className='min-w-[850px]'
      >
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className='space-y-4'>
            {fields.map((field, index) => {
              return (
                <div key={field.id} className='p-3 space-y-4 border rounded-md'>
                  <div className='flex justify-between px-1 '>
                    <h2>SN .{index + 1}</h2>
                    {/* {fields.length > 1 && ( */}
                    <Trash2
                      className='w-5 h-5 cursor-pointer text-destructive'
                      onClick={() => handleRemove(field.itemRateId, index)}
                    />
                    {/* )} */}
                  </div>
                  <div className='grid items-end grid-cols-5 gap-4'>
                    <FormField
                      name={`rates.${index}.itemId`}
                      control={form.control}
                      render={({ field }) => (
                        <FormWrapper
                          isRequired
                          label='Items'
                          className='col-span-2'
                        >
                          <FormSelectSingle
                            field={field}
                            isLoading={isLoading}
                            placeholder='Select Item'
                            options={items?.data
                              ?.filter(
                                (item) =>
                                  !selectedItems.includes(item.id) ||
                                  item.id === field.value,
                              )
                              .map((item) => ({
                                label: item?.title as string,
                                value: item?.id,
                              }))}
                            onFieldValueChange={(value) =>
                              handleSelectChange(value, index)
                            }
                          />
                        </FormWrapper>
                      )}
                    />
                    <FormField
                      name={`rates.${index}.defaultRate`}
                      control={form.control}
                      render={({ field }) => (
                        <InputField
                          placeholder='Rs.00'
                          {...field}
                          disabled
                          label='Default Rates'
                          required
                        />
                      )}
                    />
                    <FormField
                      name={`rates.${index}.currentRate`}
                      control={form.control}
                      render={({ field }) => (
                        <InputField
                          placeholder='Rs.00'
                          {...field}
                          disabled
                          label='Current Rates'
                          required
                        />
                      )}
                    />
                    <FormField
                      name={`rates.${index}.specialRate`}
                      control={form.control}
                      render={({ field }) => (
                        <InputField
                          placeholder='Rs.00'
                          {...field}
                          type='number'
                          className='pl-4 border-l'
                          label='Special Rates'
                          required
                        />
                      )}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <ButtonWithIcon
            icon='add'
            variant={"light"}
            className='mt-5'
            onClick={() => append(emptySpecialRate)}
          >
            Add New Row
          </ButtonWithIcon>
          <div className='grid grid-cols-3 gap-3 mt-8 '>
            <Button variant={"ghost"} onClick={() => form.reset()}>
              {resetBtnName}
            </Button>
            <Button
              type='submit'
              className='w-10/12 col-span-2 '
              size={"lg"}
              disabled={!form.formState.isDirty || emptyField}
              isLoading={isUpdatingCustomerRate || isUpdatingProjectRate}
            >
              {actionBtnName}
            </Button>
          </div>
        </form>
      </FormDialog>
    </Form>
  );
};

export default SpecialRateModal;
